import { AppToasterV2, CoreFieldValue, hashStringSHA256 } from "@ucl/library";
import { AxiosError } from "axios";
import { isNil } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useAutoSave } from "./useAutoSave";
import KeyPersonApiClient from "@/api/keyPersonApiClient";
import { KeyPerson } from "@/types/KeyPerson";
import { useAuthenticatedApiClient } from "./useAuthenticatedApiClient";

// Browser-compatible hash function using Web Crypto API

export const useKeyPersonDocuments = (keyPersonId: string) => {
  const [formValue, setFormValueInternal] = useState<KeyPerson | undefined>();
  // Hash of the form value
  const [formValueHash, setFormValueHash] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const keyPersonApiClient = useAuthenticatedApiClient(KeyPersonApiClient);

  // Use the generic auto-save hook
  const { markDirty } = useAutoSave({
    saveFunction: async () => {
      if (formValue) {
        await saveIdentityDocuments(formValue);
      }
    },
    debounceDelay: 1000,
  });

  useEffect(() => {
    getKeyPerson();
  }, [keyPersonId]);

  const getKeyPerson = async () => {
    setIsLoading(true);
    const keyPerson = await keyPersonApiClient.getKeyPersonById(keyPersonId);
    setFormValueInternal(keyPerson);
    const keyPersonHash = await hashStringSHA256(JSON.stringify(keyPerson));
    setFormValueHash(keyPersonHash);
    setIsLoading(false);
  };

  const setFormValue = async (value: KeyPerson, newFieldValue: CoreFieldValue) => {
    if (isNil(newFieldValue) || isNil(value.identificationDocuments) || isNil(value)) {
      return;
    }

    // Check if the form value has changed
    const newHash = await hashStringSHA256(JSON.stringify(value));
    if (formValueHash === newHash) {
      return;
    }

    // Run any UI field changes
    setFormValueInternal({ ...value });

    // Mark as dirty and trigger auto-save
    markDirty();
  };

  const saveIdentityDocuments = useCallback(
    async (data: KeyPerson) => {
      setIsSaving(true);

      try {
        const updated = await keyPersonApiClient.updateKeyPersonIdentityDocuments(
          data.financialApplicationId || "",
          keyPersonId,
          data
        );

        return updated;
      } catch (error) {
        // Skip 400 errors, validation is handled by the form
        if (error instanceof AxiosError && error.response?.status === 400) {
          return;
        }
        console.error("Error saving key person data:", error);

        AppToasterV2.showFailedActionToaster("Failed to save key person data.");
      } finally {
        setIsSaving(false);
      }
    },
    [keyPersonId, keyPersonApiClient]
  );

  return {
    formValue,
    setFormValue,
    saveIdentityDocuments,
    isSaving,
    isLoading,
  };
};
